import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Card, Checkbox, Group, Loader, Stack, Text, Title, Tooltip } from "@mantine/core";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { useEffect } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import { useDriver } from "@/contexts";
import { useSession } from "@/hooks/@custom/useSession";
import { MODAL_HANDLER } from "@/lib/Mantine";
import { GLOBAL_SCHEMA } from "@/lib/zod";
import { useGetDriverQuery, useGetQueueHasDriverQuery, useSaveTermsAuthorizationMutation } from "@/store/apis/nextcargo";

export type ContactsOutletContext = {
  data: {
    acceptedContacts: Schema["contacts"];
    terms: Partial<Pick<NextApp.Driver["authorization"][0]["useTerm"], "id" | "updatedAt">>;
    driver: NextApp.Driver | null;
    hasDriver?: boolean;
  };
  isLoading?: boolean;
  isError?: boolean;
};

type Schema = z.infer<typeof schema>;
const schema = z.object({
  contacts: GLOBAL_SCHEMA.OPTIONAL.CONTACT,
  terms: GLOBAL_SCHEMA.OPTIONAL.TERMS
});

export const Route = createFileRoute("/app/perfil")({
  component: ProfileLayout
});

export function ProfileLayout() {
  const { driver } = useSession();
  const { driverQueuePosition } = useDriver();

  const driverId = driver?.id;
  const [saveTermsAuthorization] = useSaveTermsAuthorizationMutation();
  const { data: hasDriverData, isLoading: isHasDriverDataLoading } = useGetQueueHasDriverQuery({ id: driverId! }, { skip: !driverId });
  const { data: driverData, isLoading: isDriverDataLoading, isSuccess: isDriverDataSuccess } = useGetDriverQuery({ id: driverId! }, { skip: !driverId });
  // const { data: driverQueuePositionData, isSuccess, error } = useGetQueueDriverPositionQuery({ id: driverId! }, { skip: !driverId });
  // console.log(driverQueuePositionData, { isSuccess, error });

  const isLoading = isDriverDataLoading || isHasDriverDataLoading;

  const {
    formState: { isSubmitting, isDirty },
    handleSubmit,
    control,
    reset
  } = useForm<Schema>({
    mode: "all",
    defaultValues: {
      contacts: [],
      terms: {
        id: undefined,
        updatedAt: undefined
      }
    },
    resolver: zodResolver(schema)
  });

  const onSubmit: SubmitHandler<Schema> = async (data) => {
    const { contacts, terms } = data;

    const body = {
      id: driverData?.authorization[0]?.id,
      hasEmail: contacts.includes("email"),
      hasSMS: contacts.includes("sms"),
      hasWhats: contacts.includes("whats"),
      driver: {
        id: Number(driverId)
      },
      useTerm: {
        id: terms.id,
        updatedAt: terms.updatedAt
      }
    };
    await saveTermsAuthorization(body);
  };

  const handleExitQueue = () => {
    MODAL_HANDLER.queue.deleteQueue();
  };

  useEffect(() => {
    if (!isDriverDataLoading && isDriverDataSuccess) {
      reset((values) => ({
        ...values,
        contacts: Object.entries(driverData?.authorization[0] || {})
          .map(([key, value]) => {
            if (key === "hasEmail" && value) return "email";
            if (key === "hasSMS" && value) return "sms";
            if (key === "hasWhats" && value) return "whats";
            return null;
          })
          .filter((item): item is "email" | "sms" | "whats" => item !== null),
        terms: {
          id: driverData?.authorization[0]?.useTerm?.id,
          updatedAt: driverData?.authorization[0]?.useTerm?.updatedAt as string
        }
      }));
    }
  }, [isDriverDataLoading, isDriverDataSuccess]);

  if (isLoading) return <Loader type="nextCargo" />;

  if (driverQueuePosition?.isDriverActionsBlocked || hasDriverData?.hasDriver) {
    return (
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card component={Stack} shadow="md" radius="lg" p="xl" className="max-w-md!" withBorder>
          <Stack align="center" justify="center" gap="8px" ta="center">
            <Title fw="bold" size="24px" c="gray.8">
              Autorizar métodos de contato
            </Title>
            <Text c="gray.6">Você está na fila e não pode editar seu perfil. Apenas é possível editar os métodos de contato que deseja utilizar.</Text>
          </Stack>

          <Controller
            name="contacts"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Checkbox.Group
                label="Métodos de contato"
                description="Selecione os métodos de contato que deseja autorizar"
                className="rounded-md bg-gray-300/25 p-2 shadow-sm"
                error={error?.message}
                {...field}
              >
                <Group mt={"xs"}>
                  <Checkbox label="Email" value="email" />
                  <Checkbox label="SMS" value="sms" />
                  <Checkbox label="WhatsApp" value="whats" />
                </Group>
              </Checkbox.Group>
            )}
          />

          <Stack gap="xs">
            <Tooltip label="Realize alguma alteração nos métodos de contato" disabled={isDirty}>
              <Button type="submit" loading={isSubmitting} disabled={!isDirty} color="green" fullWidth>
                Salvar
              </Button>
            </Tooltip>

            <Button color="red" fullWidth onClick={handleExitQueue}>
              Sair da fila
            </Button>
          </Stack>
        </Card>
      </form>
    );
  }

  return <Outlet />;
}
